import {
  Card,
  Box,
  ToggleSwitch,
  Dropdown,
  Layout,
  Cell,
  NumberInput,
  Button,
  Text,
  FormField,
} from "@wix/design-system";
import React, { useEffect, useState } from "react";
import * as Icons from "@wix/wix-ui-icons-common";
import { useForm, Controller } from "react-hook-form";
import "./inventorySettings.css";
import { showToast } from "@wix/dashboard-sdk";
import { useInventorySettings, useSaveInventorySettings } from "../../../hooks/useSettings";
import { convertSettingsData } from "../../../services/utils/settingsUtils";
import { IInventorySettings } from "../../../services/models/types/settings/inventorySettings.types";
import Loading from "../../common/Loader";

const exportOptions = [
  { id: "retail", value: "Retail" },
  { id: "wholesale", value: "Wholesale" },
];

const InventorySettings = ({ context = "settings", onSuccess = () => {} }) => {
  const inventorySettingsQuery = useInventorySettings();
  let inventorySettings = inventorySettingsQuery?.data?.data;
  const settingsMutation = useSaveInventorySettings();
  const [wholeSaleValue, setWholeSaleValue] = React.useState<number>(100);
  const [retailPrice, setRetailPrice] = React.useState(0);

  const defaultValues = {
    sync_quantity_to_faire: false,
    sync_product_status_to_faire: false,
    sync_deleted_product_to_faire: false,
    sync_similar_sku_in_wix: false,
    exportable_price_to_faire: "actual",
    export_price_as_to_faire: "retail",
    sync_faire_quantity_to_wix: false,
    sync_similar_sku_in_faire: false,
    exportable_price_to_wix: "retail",
    conversion_rate_to_retail_price: 10,
    ...(inventorySettings && convertSettingsData(inventorySettings, true)),
  };

  const { handleSubmit, control, watch, reset } = useForm({
    defaultValues: {
      ...defaultValues,
    },
  });

  const [isLoading, setLoading] = useState(false);

  const conversationRate = watch("conversion_rate_to_retail_price");

  React.useEffect(() => {
    let retailPrice = Number(conversationRate) * wholeSaleValue;
    if (isNaN(retailPrice)) retailPrice = 0;
    setRetailPrice(retailPrice);
  }, [conversationRate, wholeSaleValue]);

  async function onSubmit(data: IInventorySettings) {
    setLoading(true);
    try {
      await settingsMutation.mutateAsync(data);
      showToast({
        type: "success",
        message: "Settings saved successfully",
      });
      setLoading(false);
      onSuccess();
    } catch (error) {
      showToast({
        type: "error",
        message: "Sorry, couldn't save the settings",
      });
      setLoading(false);
    }
  }

  useEffect(() => {
    if (inventorySettings) {
      reset({
        ...defaultValues,
        ...convertSettingsData(inventorySettings, true),
      });
    }
  }, [inventorySettings]);

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div style={{ marginBottom: "24px" }}>
          <Card hideOverflow>
            <Card.Header title="Wix to Faire Product Settings" />
            <Card.Divider />
            <Box verticalAlign="middle" align="space-between" padding="18px 24px">
              <Box direction="vertical">
                <Text weight="bold" skin="standard">
                  Sync Wix quantity to Faire
                </Text>
                <Text secondary size="small">
                  When you update the quantity of a Wix product, it also updates the quantity of similar SKU products in
                  Faire.
                </Text>
              </Box>
              <Controller
                name="sync_quantity_to_faire"
                control={control}
                render={({ field: { value, onChange } }) => <ToggleSwitch checked={value} onChange={onChange} />}
              />
            </Box>
            <Card.Divider />
            <Box verticalAlign="middle" align="space-between" padding="18px 24px">
              <Box direction="vertical">
                <Text weight="bold" skin="standard">
                  Sync Wix product status to Faire
                </Text>
                <Text secondary size="small">
                  When you update the status of a Wix product, it also updates the status of similar SKU products in
                  Faire.
                </Text>
              </Box>
              <Controller
                name="sync_product_status_to_faire"
                control={control}
                render={({ field: { value, onChange } }) => <ToggleSwitch checked={value} onChange={onChange} />}
              />
            </Box>
            <Card.Divider />
            {/* <Box verticalAlign="middle" align="space-between" padding="18px 24px">
              <Box direction="vertical">
                <Text weight="bold" skin="standard">
                  Sync deleted product on Wix to Faire
                </Text>
                <Text secondary size="small">
                  When you delete a Wix product, it also deletes the product with a similar SKU in Faire.
                </Text>
              </Box>
              <Controller
                name="sync_deleted_product_to_faire"
                control={control}
                render={({ field: { value, onChange } }) => <ToggleSwitch checked={value} onChange={onChange} />}
              />
            </Box> */}
            <Card.Divider />
            <Box verticalAlign="middle" align="space-between" padding="18px 24px">
              <Box direction="vertical">
                <Text weight="bold" skin="standard">
                  Select whether to send the Actual price or the Discounted price to Faire
                </Text>
              </Box>
              <Controller
                name="exportable_price_to_faire"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Dropdown
                    size="large"
                    options={[
                      { id: "actual", value: "Actual" },
                      { id: "discounted", value: "Discounted" },
                    ]}
                    selectedId={value}
                    onSelect={(selectedOption: any) => onChange(selectedOption.id)}
                  />
                )}
              />
            </Box>
            <Card.Divider />
            <Box verticalAlign="middle" align="space-between" padding="18px 24px">
              <Box direction="vertical">
                <Text weight="bold" skin="standard">
                  Select the pricing option to send to your Faire store
                </Text>
              </Box>
              <Controller
                name="export_price_as_to_faire"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Dropdown
                    size="large"
                    options={exportOptions}
                    selectedId={value}
                    onSelect={(selectedOption: any) => onChange(selectedOption.id)}
                  />
                )}
              />
            </Box>
            <Card.Divider />
            <Box verticalAlign="middle" align="space-between" padding="18px 24px">
              <Box direction="vertical">
                <Text weight="bold" skin="standard">
                  Conversion rate between wholesale and retail price
                </Text>
                <Text secondary size="small">
                  Retails price = (1.25 to 10) x Wholesale price
                </Text>
              </Box>
            </Box>
            <div style={{ padding: "18px 24px" }} className="cell-wrapper">
              <Layout alignItems="center">
                <Cell span={4}>
                  <Text secondary>Wholesale</Text>
                  <NumberInput
                    className="input-with-multiple"
                    value={wholeSaleValue}
                    onChange={(value: any) => setWholeSaleValue(value)}
                  />
                </Cell>
                <Cell span={4}>
                  <FormField label="Conversion rate" infoContent="Has to be between 1.25 to 10">
                    <Controller
                      name="conversion_rate_to_retail_price"
                      control={control}
                      rules={{ min: 1.25, max: 10 }}
                      render={({ field: { value, onChange } }) => (
                        <NumberInput
                          value={value}
                          onChange={onChange}
                          min={1.25}
                          max={10}
                          statusMessage={"erorr message"}
                        />
                      )}
                    />
                  </FormField>
                </Cell>
                <Cell span={4}>
                  <Text secondary>Retail price</Text>
                  <NumberInput className="input-with-equal" value={retailPrice} readOnly />
                </Cell>
              </Layout>
            </div>
          </Card>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <Card hideOverflow>
            <Card.Header title="Faire to Wix Product Settings" />
            <Card.Divider />
            <Box verticalAlign="middle" align="space-between" padding="18px 24px">
              <Box direction="vertical">
                <Text weight="bold" skin="standard">
                  Select whether to send the Retail price or the Wholesale price to Wix
                </Text>
              </Box>
              <Controller
                name="exportable_price_to_wix"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Dropdown
                    size="large"
                    options={exportOptions}
                    selectedId={value}
                    onSelect={(selectedOption: any) => onChange(selectedOption.id)}
                  />
                )}
              />
            </Box>
            <Card.Divider />
            <Box verticalAlign="middle" align="space-between" padding="18px 24px">
              <Box direction="vertical">
                <Text weight="bold" skin="standard">
                  Sync Faire quantity to Wix
                </Text>
                <Text secondary size="small">
                  When you update the quantity of a Faire product, it also updates the quantity of similar SKU products
                  in Wix.
                </Text>
              </Box>
              <Controller
                name="sync_faire_quantity_to_wix"
                control={control}
                render={({ field: { value, onChange } }) => <ToggleSwitch checked={value} onChange={onChange} />}
              />
            </Box>
          </Card>
        </div>
        <Box>
          <Button
            type="submit"
            size="medium"
            prefixIcon={isLoading ? <Loading size="tiny" /> : context === "settings" ? <Icons.Download /> : ""}
            suffixIcon={!isLoading && context !== "settings" ? <Icons.ArrowRight /> : ""}
          >
            {context === "settings" ? "Save configurations" : "Save and Continue"}
          </Button>
        </Box>
      </form>
    </div>
  );
};

export default InventorySettings;
